import { ServerConnection, type useServer } from "@/context/server"
import type { SessionTab, useTabs } from "@/context/tabs"
import type { useCoworkWorkspace } from "./workspace-context"
import { ensureRemoteSession, getRuntimeConnection } from "./api"

type Server = ReturnType<typeof useServer>
const connections = new WeakMap<Server, Promise<ServerConnection.Http>>()

export function isTransientChatLaunchError(error: unknown) {
  if (error instanceof TypeError) return true
  const message = error instanceof Error ? error.message : String(error)
  return /failed to fetch|networkerror|load failed|timed? ?out/i.test(message)
}

export async function retryTransientChatLaunch<T>(
  operation: () => Promise<T>,
  options: { attempts?: number; delays?: number[]; sleep?: (milliseconds: number) => Promise<void> } = {},
) {
  const attempts = options.attempts ?? 3
  const delays = options.delays ?? [250, 750]
  const sleep =
    options.sleep ?? ((milliseconds) => new Promise<void>((resolve) => window.setTimeout(resolve, milliseconds)))
  let lastError: unknown
  for (let attempt = 0; attempt < attempts; attempt++) {
    try {
      return await operation()
    } catch (error) {
      lastError = error
      if (!isTransientChatLaunchError(error) || attempt === attempts - 1) throw error
      await sleep(delays[Math.min(attempt, delays.length - 1)] ?? 0)
    }
  }
  throw lastError
}

export function ensureOpenCodeServer(server: Server): Promise<ServerConnection.Http> {
  const existing = connections.get(server)
  if (existing) return existing
  // Names and saved URLs are not authoritative credentials. Only share discovery
  // while it is in flight, so later opens can refresh rotated runtime credentials.
  const pending = getRuntimeConnection()
    .then(
      (connection) =>
        server.add(
          {
            type: "http",
            http: {
              url: connection.url,
              username: connection.username || "opencode",
              password: connection.password,
            },
            displayName: connection.displayName || "Cowork Report Agent",
          },
          { activate: false },
        )!,
    )
    .finally(() => connections.delete(server))
  connections.set(server, pending)
  return pending
}

export class CoworkChatUnavailableError extends Error {
  constructor() {
    super("cowork.enterprise.chats.unavailable")
  }
}

export type CoworkChatTarget = {
  companyID: string
  projectID: string
  periodID?: string | null
  sessionID: string
  remoteSessionID?: string | null
  title?: string
}

type OpenChatDeps = {
  context: ReturnType<typeof useCoworkWorkspace>
  server: Server
  tabs: ReturnType<typeof useTabs>
  isCurrent?: () => boolean
  beforeOpen?: (tab: SessionTab) => void
}

const provisioning = new WeakMap<Server, Map<string, ReturnType<typeof ensureRemoteSession>>>()
const openings = new WeakMap<OpenChatDeps["tabs"], object>()

export async function openCoworkChat(target: CoworkChatTarget, deps: OpenChatDeps): Promise<SessionTab | undefined> {
  const { companyID, projectID, sessionID, periodID, title } = target
  const { context, server, tabs } = deps
  if (deps.isCurrent?.() === false) return
  const opening = {}
  openings.set(tabs, opening)
  const isCurrent = () => openings.get(tabs) === opening && (deps.isCurrent?.() ?? true)
  try {
    if (!isCurrent()) return
    // Select the authorized target company before provisioning, and pin it in
    // the request even if token acquisition outlives a subsequent scope change.
    context.selectEntity(companyID)
    let remoteSessionID = target.remoteSessionID
    if (!remoteSessionID) {
      let pending = provisioning.get(server)
      if (!pending) provisioning.set(server, (pending = new Map()))
      const key = JSON.stringify([companyID, projectID, sessionID])
      let session = pending.get(key)
      if (!session) {
        session = retryTransientChatLaunch(() => ensureRemoteSession(projectID, sessionID, companyID)).finally(() =>
          pending.delete(key),
        )
        pending.set(key, session)
      }
      remoteSessionID = (await session).remote_opencode_session_id
      if (!isCurrent()) return
    }
    if (!remoteSessionID) throw new CoworkChatUnavailableError()
    const connection = await retryTransientChatLaunch(() => ensureOpenCodeServer(server))
    if (!isCurrent()) return
    const scope = { companyID, projectID, periodID: periodID || context.allPeriodsID, ...(title ? { title } : {}) }
    return await tabs.openSessionTab(
      { server: ServerConnection.key(connection), sessionId: remoteSessionID },
      scope,
      () => {
        if (!isCurrent()) return false
        deps.beforeOpen?.({ type: "session", server: ServerConnection.key(connection), sessionId: remoteSessionID })
        context.selectEntity(companyID)
        context.selectProject(projectID)
        context.selectPeriod(scope.periodID)
      },
    )
  } finally {
    if (openings.get(tabs) === opening) openings.delete(tabs)
  }
}
